import { useEffect, useRef, useState } from 'react'
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/custom/button'
import { Skeleton } from '@/components/ui/skeleton'
import { CustomSelect } from '@/components/custom/custom-select'
import DataTable from '@/components/custom/data-table'
import { ChevronLeft } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { getWorkflowHistories, getWorkflow } from '@/api/workflow'
import QueryKey from '@/constants/use-query-key'
import { formatDay, formatDayWithTime } from '@/utils/date'
import { Input } from '@/components/ui/input'
import {
  WorkflowRunStatusOptions,
  WorkflowRunStatusContext,
} from '@/constants/workflow'
import useDebounce from '@/hooks/use-debounce'

export default function WorkflowHistory({ id }) {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchText, setSearchText] = useState('')
  const debounceSearchText = useDebounce(searchText, 500)
  const [filterStatus, setFilterStatus] = useState('-1')
  const [lastReloadTime, setLastReloadTime] = useState(Date.now())
  const firstMountRef = useRef(true)

  useEffect(() => {
    if (!firstMountRef.current) {
      setLastReloadTime(Date.now())
      setPage(1)
    }
  }, [debounceSearchText, filterStatus])

  const {
    isLoading: isLoadingWorkflow,
    isError,
    data: workflow,
  } = useQuery({
    enabled: !!id,
    queryKey: [QueryKey.GET_WORKFLOW_BY_ID, id],
    queryFn: async () => {
      const { data } = await getWorkflow(id)

      return data
    },
  })

  if (isError) {
    navigate({
      to: '/automation',
    })
  }

  const {
    isLoading: isLoadingWorkflowHistories,
    data: workflowHistoriesQuery,
  } = useQuery({
    queryKey: [
      QueryKey.GET_WORKFLOW_HISTORIES,
      { lastReloadTime, page, pageSize },
    ],
    queryFn: async () => {
      const params = {
        page,
        page_size: pageSize,
        search: searchText.trim(),
      }

      if (filterStatus !== '-1') {
        params.status = filterStatus
      }

      const { data } = await getWorkflowHistories({ id, params })

      firstMountRef.current = false

      return data
    },
  })

  const workflowHistories = isLoadingWorkflowHistories
    ? []
    : (workflowHistoriesQuery?.data ?? [])

  const total = isLoadingWorkflowHistories
    ? 1
    : (workflowHistoriesQuery?.total ?? 0)

  const columns = [
    {
      accessorKey: 'start_Time',
      header: () => <div>Thời gian kích hoạt</div>,
      cell: ({ row }) => {
        return formatDayWithTime(row.original.start_time)
      },
    },
    {
      accessorKey: 'code',
      header: () => <div>ID</div>,
      cell: ({ row }) => {
        let code = ''

        if (row.original.ticket) {
          code = row.original.ticket.code
        }

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="max-w-[250px] truncate" asChild>
                <p
                  className="text-sm font-medium text-primary underline hover:opacity-75"
                  onClick={() => {
                    navigate({
                      to: '/tickets/$ticketId',
                      params: {
                        ticketId: row.original.ticket._id,
                      },
                    })
                  }}
                >
                  {code}
                </p>
              </TooltipTrigger>
              <TooltipContent>{code}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      },
    },
    {
      accessorKey: 'subject',
      header: () => <div>Tiêu đề</div>,
      cell: ({ row }) => {
        let subject = ''

        if (row.original.ticket) {
          subject = row.original.ticket.subject
        }

        if (!subject) {
          return ''
        }

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="max-w-[250px] truncate">
                {subject}
              </TooltipTrigger>
              <TooltipContent>{subject}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      },
    },
    {
      accessorKey: 'requester',
      header: () => <div>Khách hàng</div>,
      cell: ({ row }) => {
        return row.original.requester ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="max-w-[250px] truncate">
                {row.original.requester.name}
              </TooltipTrigger>
              <TooltipContent>{row.original.requester.name}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null
      },
    },
    {
      accessorKey: 'action',
      header: () => <div>Hành động</div>,
      cell: ({ row }) => {
        if (
          Array.isArray(row.original.steps) &&
          row.original.steps.length > 0
        ) {
          const steps = row.original.steps
          const lastStep = steps[steps.length - 1]

          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="max-w-[250px] truncate">
                  {lastStep.step_name}
                </TooltipTrigger>
                <TooltipContent>{lastStep.step_name}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        }

        return ''
      },
    },
    {
      accessorKey: 'status',
      header: () => <div>Trạng thái</div>,
      cell: ({ row }) => {
        return WorkflowRunStatusContext[row.original.status]
      },
    },
    {
      accessorKey: 'note',
      header: () => <div>Note</div>,
      cell: ({ row }) => {
        return !row.original.error ? null : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="max-w-[250px] truncate">
                {row.original.error}
              </TooltipTrigger>
              <TooltipContent>{row.original.error}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      },
    },
  ]

  return (
    <div className="flex h-full w-full flex-col gap-2 overflow-hidden">
      <div className="sticky top-0 space-y-2">
        {isLoadingWorkflow ? (
          <>
            <Skeleton className="h-8 w-full" />
            <div className="flex gap-4">
              <Skeleton className="h-24 w-[300px]" />
              <Skeleton className="h-24 w-full" />
            </div>
          </>
        ) : (
          <>
            <div className="flex w-full items-center gap-2">
              <div className="flex flex-1 items-center gap-2 truncate">
                <ChevronLeft
                  className="h-4 w-4 shrink-0 cursor-pointer"
                  onClick={() => {
                    navigate({
                      to: `/automation/editor/${id}`,
                    })
                  }}
                />

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <h1 className="text-lg font-medium">{workflow.name}</h1>
                    </TooltipTrigger>
                    <TooltipContent>{workflow.name}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="shrink-0">
                <Button
                  size="sm"
                  onClick={() => {
                    navigate({
                      to: `/automation/editor/${id}`,
                    })
                  }}
                >
                  Hành động
                </Button>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="h-24 w-[300px] space-y-2.5 overflow-hidden rounded-lg border border-[#E5E9EF] bg-white p-4">
                <div className="flex items-center gap-2 text-sm">
                  <p className="shrink-0 font-medium text-[#000000]">
                    Tên kịch bản:
                  </p>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger className="flex-1 truncate">
                        <p>{workflow.name}</p>
                      </TooltipTrigger>
                      <TooltipContent>{workflow.name}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <p className="shrink-0 font-medium text-[#000000]">Mô tả:</p>
                  <p className="flex-1 truncate"></p>
                </div>
              </div>
              <div className="h-24 w-full space-y-2.5 overflow-hidden rounded-lg border border-[#E5E9EF] bg-white p-4">
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-1 flex gap-2 text-sm">
                    <p className="shrink-0 font-medium text-[#000000]">
                      Sự kiện:
                    </p>
                    <p className="flex-1 truncate">
                      {workflow.workflow_steps.trigger.target === 'tickets'
                        ? 'Ticket'
                        : 'Chat'}
                    </p>
                  </div>
                  <div className="col-span-1 flex gap-2 text-sm">
                    <p className="shrink-0 font-medium text-[#000000]">
                      Sự kiện kích hoạt:
                    </p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="truncate">
                          <p className="flex-1 truncate">
                            {workflow.workflow_steps.trigger.title}
                          </p>
                        </TooltipTrigger>
                        <TooltipContent>
                          {workflow.workflow_steps.trigger.title}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="col-span-1 flex gap-2 truncate text-sm">
                    <p className="shrink-0 font-medium text-[#000000]">
                      Trạng thái:
                    </p>
                    <p className="flex-1 truncate">
                      {workflow.status === 'draft'
                        ? 'Bản nháp'
                        : workflow.status === 'active'
                          ? 'Hoạt động'
                          : 'Không hoạt động'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-1 flex gap-2 text-sm">
                    <p className="shrink-0 font-medium text-[#000000]">
                      Người tạo:
                    </p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="truncate">
                          <p className="flex-1 truncate">
                            {workflow.created_by?.name}
                          </p>
                        </TooltipTrigger>
                        <TooltipContent>
                          {workflow.created_by?.name}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="col-span-1 flex gap-2 text-sm">
                    <p className="shrink-0 font-medium text-[#000000]">
                      Người cập nhật:
                    </p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="truncate">
                          <p className="flex-1 truncate">
                            {workflow.updated_by}
                          </p>
                        </TooltipTrigger>
                        <TooltipContent>{workflow.updated_by}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="col-span-1 flex gap-2 text-sm">
                    <p className="shrink-0 font-medium text-[#000000]">
                      Ngày khởi tạo:
                    </p>
                    <p className="flex-1 truncate">
                      {formatDay(workflow.created_at)}
                    </p>
                  </div>
                  <div className="col-span-1 flex gap-2 text-sm">
                    <p className="shrink-0 font-medium text-[#382323]">
                      Ngày cập nhật:
                    </p>
                    <p className="flex-1 truncate">
                      {' '}
                      {formatDay(workflow.updated_at)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <Card className="flex-1 overflow-hidden">
        <CardContent className="flex h-full flex-col gap-2 p-4">
          <div className="sticky top-0 flex">
            <h1 className="flex-1 text-lg font-bold">Lịch sử hành động</h1>
            <div className="flex flex-1 items-center gap-2">
              <Input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Nhập để tìm kiếm"
              />

              <CustomSelect
                value={filterStatus}
                defaultValue="-1"
                options={WorkflowRunStatusOptions}
                onChange={setFilterStatus}
              />
            </div>
          </div>

          <DataTable
            columns={columns}
            data={workflowHistories}
            isLoading={isLoadingWorkflowHistories}
            total={total}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </CardContent>
      </Card>
    </div>
  )
}
